import type { InternalAxiosRequestConfig } from 'axios'
import { guardarNonce, htuDe, montarProva } from '@/lib/auth/dpop/prova'

/**
 * DPoP no pedido HTTP (faixa 33 §3–§4): põe a prova no pedido, lê o nonce das respostas e reconhece o
 * `use_dpop_nonce`. QUANDO provar e o que fazer com a falha é do interceptor (`client.ts`). Ver `dpopDoPedido.md`.
 */

/**
 * Header de resposta/pedido: `AxiosHeaders.get` sem diferenciar caixa (pelo formato, como `client.ts::credencialDoPedido`,
 * não por `instanceof`); objeto cru (testes) pelo nome minúsculo.
 */
export function cabecalho(headers: unknown, nome: string): string | undefined {
  if (typeof headers !== 'object' || headers === null) return undefined
  const { get } = headers as { get?: unknown }
  const bruto: unknown =
    typeof get === 'function' ? (headers as { get(n: string): unknown }).get(nome) : (headers as Record<string, unknown>)[nome.toLowerCase()]
  return typeof bruto === 'string' ? bruto : undefined
}

/**
 * Prova NOVA no header `DPoP` (`htm` = método, `htu` = a URL que o axios chama, sem query). `sem-suporte` e base
 * sem URL http(s) absoluta saem sem prova (`'pronto'`). `'falhou'` = a chave falhou: quem chama não deixa sair.
 */
export async function provarPedido(config: InternalAxiosRequestConfig, accessToken?: string): Promise<'pronto' | 'falhou'> {
  const htu = htuDe(config.baseURL, config.url)
  if (htu === null) {
    console.warn('Base da API sem URL http(s) absoluta: o pedido sai sem prova DPoP.')
    return 'pronto'
  }
  const resultado = await montarProva({ htm: config.method ?? 'get', htu, accessToken })
  if (resultado.tipo === 'prova') config.headers.set('DPoP', resultado.prova)
  return resultado.tipo === 'falhou' ? 'falhou' : 'pronto'
}

/** Toda resposta que traz `DPoP-Nonce` (2xx ou erro) atualiza o nonce; sem o header, fica o último (faixa 33 §4). */
export function guardarNonceDa(headers: unknown): void {
  const nonce = cabecalho(headers, 'DPoP-Nonce')
  if (nonce) guardarNonce(nonce)
}

/**
 * `use_dpop_nonce` (faixa 33 §4): 400 no login/refresh (corpo) ou 401 na request (corpo, ou `WWW-Authenticate` —
 * pedido `blob` não tem corpo legível, `services/media`). Só de pedido que LEVOU prova e ainda não repetiu por isso.
 */
export function pedeNovoNonce(
  config: InternalAxiosRequestConfig,
  status: number,
  codigo: string,
  headers: unknown,
): boolean {
  if (config._repetidoPorNonce || !cabecalho(config.headers, 'DPoP')) return false
  if (status !== 400 && status !== 401) return false
  if (codigo === 'use_dpop_nonce') return true
  return status === 401 && (cabecalho(headers, 'WWW-Authenticate') ?? '').includes('error="use_dpop_nonce"')
}
